import React, { useState } from 'react';
import { useConvexBanners } from '../../../services/convexService';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Trash2, Edit2, Plus, Megaphone, Image as ImageIcon, Text, ExternalLink, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Id } from '../../../../backend/_generated/dataModel';

interface BannerFormProps {
    initialData?: any;
    onSubmit: (data: any) => void;
    onCancel: () => void;
    isLoading: boolean;
}

const BannerForm: React.FC<BannerFormProps> = ({ initialData, onSubmit, onCancel, isLoading }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        ar: initialData?.ar || '',
        en: initialData?.en || '',
        fr: initialData?.fr || '',
        image: initialData?.image || '',
        type: initialData?.type || 'text',
        link: initialData?.link || '',
        isActive: initialData?.isActive ?? true,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.ar || !formData.en || !formData.fr) {
            toast.error(t('banner.error.fillAll'));
            return;
        }
        if (formData.type === 'image' && !formData.image) {
            toast.error(t('banner.error.imageRequired'));
            return;
        }
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 border p-4 rounded-lg bg-muted/20">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="type">{t('common.type') || 'Type'}</Label>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant={formData.type === 'text' ? 'default' : 'outline'}
                            className="flex-1 gap-2"
                            onClick={() => setFormData({ ...formData, type: 'text' })}
                        >
                            <Text className="w-4 h-4" />
                            {t('common.text') || 'Text'}
                        </Button>
                        <Button
                            type="button"
                            variant={formData.type === 'image' ? 'default' : 'outline'}
                            className="flex-1 gap-2"
                            onClick={() => setFormData({ ...formData, type: 'image' })}
                        >
                            <ImageIcon className="w-4 h-4" />
                            {t('common.image') || 'Image'}
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="link">{t('banner.link')}</Label>
                    <Input
                        id="link"
                        value={formData.link}
                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                        placeholder={t('banner.linkPlaceholder')}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="ar">{formData.type === 'image' ? t('banner.altTextAr') : t('banner.contentAr')}</Label>
                <Input
                    id="ar"
                    value={formData.ar}
                    onChange={(e) => setFormData({ ...formData, ar: e.target.value })}
                    dir="rtl"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="en">{formData.type === 'image' ? t('banner.altTextEn') : t('banner.contentEn')}</Label>
                <Input
                    id="en"
                    value={formData.en}
                    onChange={(e) => setFormData({ ...formData, en: e.target.value })}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="fr">{formData.type === 'image' ? t('banner.altTextFr') : t('banner.contentFr')}</Label>
                <Input
                    id="fr"
                    value={formData.fr}
                    onChange={(e) => setFormData({ ...formData, fr: e.target.value })}
                />
            </div>

            {formData.type === 'image' && (
                <div className="space-y-2">
                    <Label htmlFor="image">{t('banner.imageUrl')}</Label>
                    <Input
                        id="image"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        placeholder="https://..."
                    />
                </div>
            )}

            <div className="flex items-center gap-2">

                <Label htmlFor="isActive">{t('common.active') || 'Active'}</Label>
            </div>

            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                    <X className="w-4 h-4 mr-2" />
                    {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={isLoading}>
                    <Save className="w-4 h-4 mr-2" />
                    {initialData ? t('common.update') : t('common.create')}
                </Button>
            </div>
        </form>
    );
};

export const BannerManagement: React.FC = () => {
    const { t, language } = useLanguage();
    const { getAllBanners, createBanner, updateBanner, deleteBanner } = useConvexBanners();
    const banners = getAllBanners();

    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<Id<'banners'> | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCreate = async (data: any) => {
        setIsSubmitting(true);
        try {
            await createBanner(data);
            toast.success(t('banner.success.created'));
            setIsAdding(false);
        } catch (error) {
            toast.error(t('banner.error.createFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdate = async (data: any) => {
        if (!editingId) return;
        setIsSubmitting(true);
        try {
            await updateBanner({ bannerId: editingId, ...data });
            toast.success(t('banner.success.updated'));
            setEditingId(null);
        } catch (error) {
            toast.error(t('banner.error.updateFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (bannerId: Id<'banners'>) => {
        if (window.confirm(t('banner.deleteConfirm'))) {
            try {
                await deleteBanner({ bannerId });
                toast.success(t('banner.success.deleted'));
            } catch (error) {
                toast.error(t('banner.error.deleteFailed'));
            }
        }
    };

    const handleToggleActive = async (bannerId: Id<'banners'>, isActive: boolean) => {
        try {
            await updateBanner({ bannerId, isActive: !isActive });
            toast.success(!isActive ? t('banner.success.activated') : t('banner.success.deactivated'));
        } catch (error) {
            toast.error(t('banner.error.toggleFailed'));
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-xl flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-primary" />
                        {t('dashboard.admin.bannerManagement') || 'Banner Management'}
                    </CardTitle>
                    {!isAdding && !editingId && (
                        <Button onClick={() => setIsAdding(true)} className="gap-2">
                            <Plus className="w-4 h-4" />
                            {t('common.add') || 'Add Banner'}
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {isAdding && (
                        <div className="mb-6">
                            <h3 className="font-semibold mb-2">{t('common.add') || 'Add New Banner'}</h3>
                            <BannerForm
                                onSubmit={handleCreate}
                                onCancel={() => setIsAdding(false)}
                                isLoading={isSubmitting}
                            />
                        </div>
                    )}

                    <div className="space-y-4">
                        {banners === undefined ? (
                            <div className="text-center py-8">{t('banner.loading')}</div>
                        ) : banners.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                {t('banner.noBanners')}
                            </div>
                        ) : (
                            banners.map((banner) => (
                                <div key={banner._id} className="space-y-4">
                                    {editingId === banner._id ? (
                                        <BannerForm
                                            initialData={banner}
                                            onSubmit={handleUpdate}
                                            onCancel={() => setEditingId(null)}
                                            isLoading={isSubmitting}
                                        />
                                    ) : (
                                        <div className={`p-4 border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${!banner.isActive ? 'bg-muted/50 opacity-70' : 'bg-card'}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {banner.type === 'text' ? (
                                                        <Text className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                    <span className="font-semibold truncate">
                                                        {banner[language as 'ar' | 'en' | 'fr'] || banner.en}
                                                    </span>
                                                    <Badge variant={banner.isActive ? 'default' : 'outline'} className="text-[10px] py-0">
                                                        {banner.isActive ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                                                    </Badge>
                                                </div>
                                                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                                                    {banner.link && (
                                                        <span className="flex items-center gap-1">
                                                            <ExternalLink className="w-3 h-3" />
                                                            {banner.link}
                                                        </span>
                                                    )}
                                                    <span>{t('common.type')}: {banner.type}</span>
                                                </div>
                                                {banner.type === 'image' && banner.image && (
                                                    <div className="mt-2 h-12 w-32 border rounded overflow-hidden bg-black/5">
                                                        <img src={banner.image} alt="" className="h-full w-full object-contain" />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={banner.isActive}
                                                    onCheckedChange={() => handleToggleActive(banner._id, banner.isActive)}
                                                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                                                />
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => setEditingId(banner._id)}
                                                >
                                                    <Edit2 className="w-4 h-4 text-blue-500" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(banner._id)}
                                                >
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default BannerManagement;
